import { observable, action, decorate } from 'mobx';
import * as XLSX from 'xlsx-js-style';
import api from "../../utils/api";
import { GenericResponse } from "../models/genericResponseModels";

export interface LicenseData {
    Name: string;
    Email: string;
    Title: string;
    Department: string;
    ManagerName: string;
    ManagerEmail: string;
    isValid?: boolean;
    validationError?: string;
}

type Order = 'asc' | 'desc';

class LicenseImportStore {
    licenseData: LicenseData[] = [];
    isPreview: boolean = false;
    page: number = 0;
    rowsPerPage: number = 10;
    order: Order = 'asc';
    orderBy: keyof LicenseData = 'Name';
    isUploadSuccessful: boolean = false;

    setLicenseData(data: LicenseData[]) {
        this.licenseData = data;
    }

    setIsPreview(value: boolean) {
        this.isPreview = value;
    }

    setPage(value: number) {
        this.page = value;
    }

    setRowsPerPage(value: number) {
        this.rowsPerPage = value;
    }

    setOrder(value: Order) {
        this.order = value;
    }

    setOrderBy(value: keyof LicenseData) {
        this.orderBy = value;
    }

    setIsUploadSuccessful(value: boolean) {
        this.isUploadSuccessful = value;
    }

    validateData(data: LicenseData[]) {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        const emailSet = new Set<string>();
        const managerEmailMap = new Map<string, string>();

        data.forEach((record) => {
            const { Name, Email, Department, ManagerName, ManagerEmail } = record;

            record.isValid = true;
            record.validationError = '';

            if (!Name?.trim() || !Email?.trim() || !ManagerName?.trim() || !ManagerEmail?.trim() || !Department?.trim()) {
                record.isValid = false;
                record.validationError = 'Fields cannot be empty';
                return;
            }

            if (!emailRegex.test(Email) || !emailRegex.test(ManagerEmail)) {
                record.isValid = false;
                record.validationError = 'Invalid email format';
                return;
            }

            if (emailSet.has(Email)) {
                record.isValid = false;
                record.validationError = 'Duplicate email';
                return;
            }
            emailSet.add(Email);

            if (managerEmailMap.has(ManagerEmail) && managerEmailMap.get(ManagerEmail) !== ManagerName) {
                record.isValid = false;
                record.validationError = 'Manager email duplicated with different manager name';
                return;
            }
            managerEmailMap.set(ManagerEmail, ManagerName);
        });
    }

    handleFileChange(file: File, onSuccess: () => void) {
        const reader = new FileReader();
        reader.onload = (e) => {
            const data = new Uint8Array(e.target?.result as ArrayBuffer);
            const workbook = XLSX.read(data, { type: 'array' });
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const jsonData: LicenseData[] = XLSX.utils.sheet_to_json(worksheet);
            this.validateData(jsonData);
            this.setLicenseData(jsonData);
            this.setIsPreview(true);
            this.setIsUploadSuccessful(false);
            onSuccess();
        };
        reader.readAsArrayBuffer(file);
    }

    async uploadLicenseData(data: LicenseData[]) {
        try {
            const response = await api.post<GenericResponse>('/License/SaveData', data, {
                headers: {
                    //'Content-Type': 'multipart/form-data',
                },
                timeout: 30 * 60E3,
            });

            if (response.data.isSuccessful) {
                this.setIsUploadSuccessful(true);
            } else {
                this.setIsUploadSuccessful(false);
            }
        } catch (error) {
            console.error('Failed to upload data:', error);
            this.setIsUploadSuccessful(false);
        }
    }
}

decorate(LicenseImportStore, {
    licenseData: observable,
    isPreview: observable,
    page: observable,
    rowsPerPage: observable,
    order: observable,
    orderBy: observable,
    isUploadSuccessful: observable,
    setLicenseData: action,
    setIsPreview: action,
    setPage: action,
    setRowsPerPage: action,
    setOrder: action,
    setOrderBy: action,
    setIsUploadSuccessful: action,
    validateData: action,
    handleFileChange: action,
    uploadLicenseData: action,
});

const licenseImportStore = new LicenseImportStore();
export default licenseImportStore;